(() => {
  const PRICE_SELECTOR = '[data-rpg-super-disc-price]';
  const ELIGIBLE_CONTENTS = new Set(['loose', 'game-only', 'disc-only']);
  const MAX_PRICE_CENTS = 500;
  const FALLBACK_PERCENT = 45;
  let refreshTimer;
  let refreshFrame;
  let contextRequestSequence = 0;
  const pendingPriceScopes = new Set();

  const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
  });

  const context = () => document.querySelector('[data-rpg-super-disc-context]');
  const unlockedPlatforms = () => new Set(
    String(context()?.dataset.rpgSuperDiscPlatforms || '')
      .split('|')
      .map((value) => value.trim())
      .filter(Boolean),
  );

  const discountedPrice = (priceCents) => {
    const percent = Number(context()?.dataset.rpgSuperDiscPercent || FALLBACK_PERCENT);
    return Math.ceil(priceCents * (100 - percent) / 100);
  };

  const refreshPrices = (scope = document) => {
    const platforms = unlockedPlatforms();
    const prices = [];
    if (scope.matches?.(PRICE_SELECTOR)) prices.push(scope);
    scope.querySelectorAll?.(PRICE_SELECTOR).forEach((price) => prices.push(price));
    prices.forEach((price) => {
      const priceCents = Number(price.dataset.rpgPriceCents || 0);
      const eligible = platforms.has(price.dataset.rpgPlatform || '')
        && price.dataset.rpgItemType === 'game'
        && price.dataset.rpgMediaFormat === 'disc'
        && ELIGIBLE_CONTENTS.has(price.dataset.rpgContents || '')
        && priceCents > 0
        && priceCents < MAX_PRICE_CENTS;
      const deal = price.querySelector('[data-rpg-super-disc-discount]');
      const discounted = deal?.querySelector('[data-rpg-super-disc-discounted]');
      price.classList.toggle('rpg-super-disc-price-active', eligible);
      if (deal) deal.hidden = !eligible;
      if (eligible && discounted) {
        const formattedPrice = money.format(discountedPrice(priceCents) / 100);
        if (discounted.textContent !== formattedPrice) discounted.textContent = formattedPrice;
      }
    });
  };

  const refreshContext = async () => {
    const requestSequence = ++contextRequestSequence;
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(async () => {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const separator = root.includes('?') ? '&' : '?';
        const response = await fetch(`${root}${separator}section_id=rpg-super-disc-deal-context&rpg_cart_refresh=${Date.now()}`, {
          cache: 'no-store',
          credentials: 'same-origin',
          headers: {'X-Requested-With': 'XMLHttpRequest'},
        });
        if (!response.ok) return;
        const html = await response.text();
        if (requestSequence !== contextRequestSequence) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const next = parsed.querySelector('[data-rpg-super-disc-context]');
        const current = context();
        if (next && current) {
          current.dataset.rpgSuperDiscPlatforms = next.dataset.rpgSuperDiscPlatforms || '|';
          current.dataset.rpgSuperDiscPercent = next.dataset.rpgSuperDiscPercent || String(FALLBACK_PERCENT);
        }
        refreshPrices();
      } catch (error) {
        // The server-rendered price remains the safe fallback if the refresh is interrupted.
      }
    }, 80);
  };

  const queuePriceRefresh = (scope = document) => {
    const usableScope = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    pendingPriceScopes.add(usableScope);
    if (refreshFrame) return;
    refreshFrame = window.requestAnimationFrame(() => {
      refreshFrame = 0;
      const scopes = Array.from(pendingPriceScopes);
      pendingPriceScopes.clear();
      if (scopes.includes(document)) {
        refreshPrices();
        return;
      }
      scopes.forEach((pendingScope) => refreshPrices(pendingScope));
    });
  };

  document.addEventListener('DOMContentLoaded', () => queuePriceRefresh(), {once: true});
  document.addEventListener('on:cart:add', refreshContext);
  document.addEventListener('on:line-item:change', refreshContext);
  document.addEventListener('dispatch:cart-drawer:refresh', refreshContext);
  document.addEventListener('on:variant:change', () => queuePriceRefresh());
  document.addEventListener('on:facet-filters:updated', () => queuePriceRefresh());

  new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.matches?.(PRICE_SELECTOR)) {
          queuePriceRefresh(node);
        } else if (node.querySelector?.(PRICE_SELECTOR)) {
          queuePriceRefresh(node);
        }
      });
    });
  }).observe(document.body || document.documentElement, {
    childList: true,
    subtree: true,
  });

  window.RPGSuperDiscDealPrices = {refresh: refreshPrices, refreshContext};
})();
